import { Switch, RouteProps, Route, Redirect, useLocation, useRouteMatch } from 'react-router-dom';

import ApplicationSettings from './pages/ApplicationSettings';
import AccountSettings from './pages/AccountSettings';
import UserSettings from './pages/UserSettings';
import ApplicationPlan from './pages/ApplicationPlan';
import ApplicationDashboard from './pages/ApplicationDashboard';
import ApplicationReport from './pages/ApplicationReport';
import Home from './pages/Home';
import Registration from './pages/Registration';
import ApplicationCreation from './pages/ApplicationCreation';
import Bibles from './pages/Bibles';
import ApiReference from './pages/ApiReference';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import SuperAdminApplications from './pages/SuperAdminApplications';
import SuperAdminPlanSettings from './pages/SuperAdminPlanSettings';

import Container from './components/Container';
import Nav from './components/Nav';
import Footer from './components/Footer';
import { LoadingPage } from './components/Loading';

import { useApplications } from './contexts/Applications';
import { useAuth } from './contexts/Auth';
import { useAccountUser } from './contexts/User';

export default function Routes() {
  const location = useLocation();
  const { authData } = useAuth();
  const { user, registrationCompleted } = useAccountUser();
  const isRegistrationPage = location.pathname.includes('/registration');

  const isAuthed = Boolean(authData);
  const isSuperAdmin = user?.isSuperAdmin ?? false;

  if (isAuthed && user && !registrationCompleted && !isRegistrationPage) {
    return <Redirect to="/registration" />;
  }

  return (
    <>
      <Nav />
      <Switch>
        <AuthRoute isAuthed={isAuthed} path="/application/create">
          <ApplicationCreation />
        </AuthRoute>

        <AuthAppRoute isAuthed={isAuthed} path="/application">
          {ApplicationDashboard}
        </AuthAppRoute>

        <AuthRoute isAuthed={isAuthed} path="/account-settings">
          <AccountSettings />
        </AuthRoute>

        <AuthRoute isAuthed={isAuthed} path="/user-settings">
          <UserSettings />
        </AuthRoute>

        <AuthRoute isAuthed={isAuthed} path="/registration">
          <Registration />
        </AuthRoute>

        <Route path="/bibles" exact component={Bibles} />
        <Route path="/api-reference" exact component={ApiReference} />

        {isSuperAdmin && (
          <>
            <AuthRoute isAuthed={isAuthed} exact path="/super-admin/dashboard">
              <SuperAdminDashboard />
            </AuthRoute>
            <AuthRoute isAuthed={isAuthed} exact path="/super-admin/applications">
              <SuperAdminApplications />
            </AuthRoute>
            <AuthRoute isAuthed={isAuthed} exact path="/super-admin/plan-settings">
              <SuperAdminPlanSettings />
            </AuthRoute>
          </>
        )}

        <Route path="/authenticated" exact component={RegistrationRedirect} />

        {/* Not the actual routes, these are needed to keep 404 page from appearing
            when the user has successfully authenticated, Amplify has unmounted its
            components and we're waiting on the user data to return from the backend
        */}
        <Route path="/sign-up" exact component={LoadingPage} />
        <Route path="/sign-in" exact component={LoadingPage} />

        <Route path="/" exact component={Home} />
        <Route path="/">
          <Container className="text-4xl">Page Not Found</Container>
        </Route>
      </Switch>

      <Footer />
    </>
  );
}

interface AuthRouteProps extends RouteProps {
  isAuthed: boolean;
}

const AuthRoute = ({ isAuthed, children, ...props }: AuthRouteProps) => (
  <Route
    {...props}
    render={({ location }) => {
      if (!isAuthed) {
        return <Redirect to={{ pathname: '/sign-in', state: { from: location } }} />;
      }

      return children;
    }}
  />
);

interface AuthAppRouteProps extends AuthRouteProps {
  loadingComponent?: any;
  children: any;
  pathMatcher?: string; // same matcher string as https://reactrouter.com/web/api/Hooks/useroutematch
}

// Once applications have been loaded,
// and there's no application id in the last part of the url
// redirect the current route to the same route with the defaultApplicationId appended to the end

const AuthAppRoute = ({
  isAuthed,
  pathMatcher,
  children: Children,
  loadingComponent,
  ...props
}: AuthAppRouteProps) => {
  // make sure that we only use this component using a matcher that will provide an appId
  // eslint-disable-next-line no-param-reassign
  if (pathMatcher === undefined) pathMatcher = `${props.path}/:appId/:menuItem?`;
  if (!pathMatcher.includes(':appId')) throw new Error('must use a matcher containing :appId');

  const { loaded, defaultApplicationId, applicationsById } = useApplications();
  const match = useRouteMatch<{ appId?: string; menuItem?: string }>(pathMatcher);
  const { appId = '', menuItem = 'dashboard' } = match?.params || ({} as any);

  const { acceptedLatestTermsAndConditions } = useAccountUser();

  return (
    <Route
      {...props}
      render={({ location }) => {
        if (!isAuthed) {
          return <Redirect to={{ pathname: '/sign-in', state: { from: location } }} />;
        }

        if (!acceptedLatestTermsAndConditions) {
          return <Redirect to={{ pathname: '/account-settings', state: { from: location } }} />;
        }

        // don't redirect if there is an app id in the url or we haven't loaded the applications yet
        if (!loaded) return loadingComponent || <LoadingPage />;

        if (Object.keys(applicationsById).length === 0) {
          return <Redirect to="/application/create" />;
        }

        if (appId) {
          if (appId in applicationsById) {
            switch (menuItem) {
              case 'plan':
                return <ApplicationPlan appId={appId} />;
              case 'reports':
                return <ApplicationReport appId={appId} />;
              case 'settings':
                return <ApplicationSettings appId={appId} />;
              default:
                return <Children appId={appId} />;
            }
          }
          return <Container>Not found</Container>;
        }

        return (
          <Redirect
            to={(pathMatcher as string)
              .replace(':appId', defaultApplicationId)
              .replace(':menuItem', menuItem)}
          />
        );
      }}
    />
  );
};

const RegistrationRedirect = () => {
  const { registrationCompleted } = useAccountUser();
  if (registrationCompleted) return <Redirect to="/application" />;
  return <Redirect to="/registration" />;
};
